import {ROUTE} from '@/constants/main/route';
import {useNamedNavigate} from '@/hooks/channel/useNamedNavigate';
import {
  useDeleteFavoriteMutation,
  useFavoriteMutation,
} from '@/query/channel/board/useFavoriteMutation';
import {useFavoriteQuery} from '@/query/channel/board/useFavoriteQuery';
import globalDomains from '@/utils/global-domains';
import {getUserLoginId, goLogin, isLogin} from '@/utils/main';
import throwToast from '@/utils/ui/toast';
import {IctProps, SoopIctWrapper, useConfirm} from '@soop-ui/soop-ui';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {isMobile} from 'react-device-detect';
import {useStationData} from '@/stores/channel/station';
import {ChannelT} from '@/utils/i18n/channel';

interface ChannelIctWrapperProps extends Omit<IctProps, 'isMe' | 'isFavorite'> {
  children: React.ReactNode;
  isMe?: boolean;
  isFavorite?: boolean;
  replace?: boolean;
  style?: React.CSSProperties;
  isVod?: boolean;
}

/**
 * 방송국을 위한 ICT 래퍼 컴포넌트
 *
 * 방송국 전용 즐겨찾기 API를 사용하여 ICT 메뉴를 제공함
 * 각 서비스에서는 해당 서비스의 API를 사용하여 다시 래핑해야 함
 *
 * @param props - 컴포넌트 props
 * @returns ICT 래퍼 컴포넌트 반환
 */
const ChannelIctWrapper = ({
  children,
  userId,
  isFavorite: initialIsFavorite,
  isMe = false,
  replace = true,
  style,
  isVod = false,
  ...props
}: ChannelIctWrapperProps) => {
  const {mutateAsync: addFavorite} = useFavoriteMutation();
  const {mutateAsync: deleteFavorite} = useDeleteFavoriteMutation();
  const loggedInUserId = getUserLoginId();
  const isMyChannel = loggedInUserId ? loggedInUserId === userId : isMe;
  const namedNavigate = useNamedNavigate();
  const {station} = useStationData();
  const {boardNumber} = useParams();

  const [isFavorite, setIsFavorite] = useState(initialIsFavorite || false);

  const {refetch: refetchFavoriteState} = useFavoriteQuery(userId);

  const handleOpenChange = async (open: boolean) => {
    if (open && typeof initialIsFavorite === 'undefined') {
      const result = await refetchFavoriteState();
      setIsFavorite(result.data?.isFavorite || false);
    }
  };

  const {openConfirm} = useConfirm();

  const handleFavoriteClick = async () => {
    if (!isLogin()) {
      goLogin();
      return;
    }

    if (isFavorite) {
      const isOk = await openConfirm({
        message: ChannelT('해당 스트리머를 즐겨찾기에서 삭제하시겠습니까?'),
      });

      if (isOk) {
        await deleteFavorite({userId});
        setIsFavorite(false);
      }
    } else {
      await addFavorite({streamerId: userId || ''});
      setIsFavorite(true);
    }
  };

  const handleSearchClick = () => {
    if (isVod) {
      namedNavigate(
        ROUTE.CHANNEL.SERVICE.VOD.ALL,
        {streamerId: station.detail?.userId || ''},
        {replace},
        {keyword: userId, isUserSearch: true},
      );
    } else {
      namedNavigate(
        ROUTE.CHANNEL.SERVICE.BOARD.BOARD_INDEX,
        {
          streamerId: station.detail?.userId || '',
          boardNumber: boardNumber || '',
        },
        {replace},
        {keyword: userId},
      );
    }
  };

  const handleHomeClick = () => {
    window.open(`${globalDomains.WWW_AFREECATV}/station/${userId}`, '_blank');
  };

  const handleNoteClick = () => {
    if (!isLogin()) {
      goLogin();
      return;
    }

    if (isMobile) {
      window.open(`${globalDomains.M_AFREECATV}/#/note/write2/${userId}`);
      return;
    }

    window.open(
      `${globalDomains.NOTE_AFREECATV}/app/index.php?page=write&targetUserId=${userId}`,
      '_blank',
      'top=10, left=10, width=500, height=600',
    );
  };

  return (
    <SoopIctWrapper
      isMe={isMyChannel}
      isFavorite={isFavorite || false}
      userId={userId}
      onFavoriteClick={handleFavoriteClick}
      onOpenChange={handleOpenChange}
      onSearchClick={handleSearchClick}
      onHomeClick={handleHomeClick}
      onNoteClick={handleNoteClick}
      style={style}
      customTitle={{
        search: isVod ? ChannelT('VOD 검색') : ChannelT('게시글 검색'),
      }}
      {...props}>
      <div style={{cursor: 'pointer'}}>{children}</div>
    </SoopIctWrapper>
  );
};

export default ChannelIctWrapper;
